import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { generate, correction, mode } from 'lean-qr'
import { buildPlaqueContours, toPlaqueUrl } from './stlExport'
import type { QrMatrix } from './stlExport'

// Physical size the STL uses — contours from buildPlaqueContours are in this space.
const MODEL_SIZE_MM = 50

// High-res size for PNG/SVG export — matches qr-code-styling's EXPORT_SIZE.
const EXPORT_SIZE = 512

export type PlaquePreviewHandle = {
  download: (extension: 'svg' | 'png', filename?: string) => Promise<void>
}

type Props = {
  url: string
  size: number // canvas pixel size (square)
}

// ---------------------------------------------------------------------------
// Shared rendering helpers
// ---------------------------------------------------------------------------

// Margin as a fraction of total size — matches qr-code-styling preview margin.
const MARGIN_RATIO = 0.036

/**
 * Coordinate transform: STL contour point → canvas pixel.
 * STL space is Y-up (0 at bottom); canvas is Y-down (0 at top).
 */
function stlToCanvas(
  sx: number, sy: number,
  margin: number, scale: number,
): [number, number] {
  return [
    margin + sx * scale,
    margin + (MODEL_SIZE_MM - sy) * scale,
  ]
}

/** Draw all plaque contours onto a canvas context at the given pixel size. */
function renderToCanvas(ctx: CanvasRenderingContext2D, matrix: QrMatrix, size: number): void {
  const margin = size * MARGIN_RATIO
  const scale = (size - 2 * margin) / MODEL_SIZE_MM

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, size, size)

  ctx.fillStyle = '#000000'
  const groups = buildPlaqueContours(matrix)
  for (const group of groups) {
    const contour = group[0]
    const path = new Path2D()
    for (let i = 0; i < contour.length; i++) {
      const [cx, cy] = stlToCanvas(contour[i][0], contour[i][1], margin, scale)
      if (i === 0) path.moveTo(cx, cy)
      else path.lineTo(cx, cy)
    }
    path.closePath()
    ctx.fill(path, 'nonzero')
  }
}

/** Build a minimal SVG string from all plaque contours at the given pixel size. */
function renderToSvg(matrix: QrMatrix, size: number): string {
  const margin = size * MARGIN_RATIO
  const scale = (size - 2 * margin) / MODEL_SIZE_MM

  const groups = buildPlaqueContours(matrix)
  const paths = groups.map((group) => {
    const contour = group[0]
    const d = contour.map(([sx, sy], i) => {
      const [cx, cy] = stlToCanvas(sx, sy, margin, scale)
      return `${i === 0 ? 'M' : 'L'}${cx.toFixed(3)},${cy.toFixed(3)}`
    }).join(' ') + ' Z'
    return `<path d="${d}" fill="#000000"/>`
  }).join('\n  ')

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">`,
    `  ${paths}`,
    `</svg>`,
  ].join('\n')
}

function triggerDownload(href: string, filename: string): void {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a pixel-accurate preview of the raised-module STL by drawing the
 * exact same contours that buildPlaqueContours() produces — circles for data
 * modules, C-shaped bridged rings + rounded-square centers for finder eyes.
 *
 * This is what the physical print will look like viewed from above, including
 * the bridge gaps in the finder rings.
 */
const PlaquePreview = forwardRef<PlaquePreviewHandle, Props>(({ url, size }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const getMatrix = () => generate(mode.alphaNumeric(toPlaqueUrl(url)), {
    minCorrectionLevel: correction.L,
    maxCorrectionLevel: correction.L,
    maxVersion: 1,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    renderToCanvas(ctx, getMatrix(), size)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, size])

  useImperativeHandle(ref, () => ({
    download: async (extension: 'svg' | 'png', filename = 'qr-code'): Promise<void> => {
      const matrix = getMatrix()

      if (extension === 'svg') {
        const svg = renderToSvg(matrix, EXPORT_SIZE)
        const blob = new Blob([svg], { type: 'image/svg+xml' })
        const href = URL.createObjectURL(blob)
        triggerDownload(href, `${filename}.svg`)
        URL.revokeObjectURL(href)
        return
      }

      // PNG: render at export resolution onto an offscreen canvas
      const offscreen = document.createElement('canvas')
      offscreen.width = EXPORT_SIZE
      offscreen.height = EXPORT_SIZE
      const ctx = offscreen.getContext('2d')!
      renderToCanvas(ctx, matrix, EXPORT_SIZE)
      return new Promise<void>((resolve) => {
        offscreen.toBlob((blob) => {
          if (!blob) { resolve(); return }
          const href = URL.createObjectURL(blob)
          triggerDownload(href, `${filename}.png`)
          URL.revokeObjectURL(href)
          resolve()
        }, 'image/png')
      })
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [url])

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block', width: size, height: size }}
    />
  )
})

PlaquePreview.displayName = 'PlaquePreview'

export default PlaquePreview
